/**
 * Generic React Error Boundary mit Newton-Branding-konformer Fallback-UI.
 *
 * Wird in InvestorDrawer eingesetzt, damit ein einzelner fehlerhafter Investor
 * (z. B. unerwartet null-Felder, Race-Condition) NICHT die ganze App killt,
 * sondern dem User eine reproduzierbare Fehler-ID zeigt, die er Heinrichs melden kann.
 *
 * Verwendung:
 *   <ErrorBoundary contextLabel="Investor-Drawer" contextId={investorId}>
 *     <InvestorDrawer ... />
 *   </ErrorBoundary>
 */
import React from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Sichtbar in Browser-DevTools-Console, damit Heinrichs uns Screenshots schicken kann
    // eslint-disable-next-line no-console
    console.error(
      `[ErrorBoundary] ${this.props.contextLabel || "Komponente"} crashed` +
        (this.props.contextId ? ` (ID: ${this.props.contextId})` : ""),
      error,
      errorInfo
    );
    this.setState({ errorInfo });
  }

  reset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
    this.props.onReset?.();
  };

  render() {
    if (this.state.hasError) {
      const { contextLabel, contextId } = this.props;
      const err = this.state.error;
      return (
        <div
          data-testid="error-boundary-fallback"
          className="p-6 space-y-4 font-sans text-navy"
        >
          <div className="flex items-start gap-3 border-l-4 border-tot bg-ledger/50 px-4 py-3">
            <AlertTriangle size={20} className="text-tot shrink-0 mt-0.5" strokeWidth={1.7} />
            <div className="flex-1">
              <div className="pb-eyebrow text-tot">Fehler aufgetreten</div>
              <div className="font-display text-navy text-lg mt-1 tracking-tight">
                {contextLabel || "Ein Fehler ist aufgetreten"}
              </div>
              <p className="text-sm text-navy/70 mt-2">
                Bitte melde diesen Vorfall an Heinrichs mit den folgenden Angaben — dann
                können wir die Ursache reproduzieren und fixen.
              </p>
            </div>
          </div>

          <div className="border border-navy/15 px-4 py-3 text-sm space-y-2">
            {contextId && (
              <div>
                <div className="pb-label">Datensatz-ID</div>
                <div data-testid="error-context-id" className="font-mono text-xs text-navy break-all select-all">
                  {contextId}
                </div>
              </div>
            )}
            <div>
              <div className="pb-label">Fehlermeldung</div>
              <pre
                data-testid="error-message"
                className="text-xs text-navy/85 whitespace-pre-wrap bg-ledger/40 p-2 border border-navy/10 overflow-x-auto"
              >
                {err?.message || String(err) || "Unbekannter Fehler"}
              </pre>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-1">
            <button
              data-testid="error-reset-btn"
              onClick={this.reset}
              className="pb-btn-outline text-xs uppercase tracking-widish font-semibold gap-2"
            >
              <RefreshCw size={13} /> Erneut versuchen
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
